// The Network screen's data. Query keys start with the entity kind ('contact', 'importBatch', 'match'), so a change event
// of that kind refetches them (lib/events.ts). Names and company texts travel only in a POST body, never in a URL.
import { infiniteQueryOptions, queryOptions } from '@tanstack/react-query'
import type { ContactHit, ContactMatch, LeadRow } from '@nw/shared/api/match'
import type { Contact, ImportBatch } from '@nw/shared/api/my'
import { api, apiPath } from '../../lib/api.ts'

const CONTACT_ID = /^ct_[0-9a-hjkmnp-tv-z]{26}$/

/** Router validateSearch: the selected contact, if it is a contact id (ids only in a URL). */
export function parseSearch(search: Record<string, unknown>): { contact?: string } {
  return typeof search.contact === 'string' && CONTACT_ID.test(search.contact) ? { contact: search.contact } : {}
}

interface Page<T> {
  items: T[]
  next: string | null
}

export const contactsQuery = infiniteQueryOptions({
  queryKey: ['contact', 'list'] as const,
  initialPageParam: undefined as string | undefined,
  queryFn: ({ pageParam, signal }) => api.get<Page<Contact>>(apiPath('/api/my/contacts', {}, { after: pageParam, limit: 200 }), signal),
  getNextPageParam: (last) => last.next ?? undefined,
})

export const contactQuery = (id: string) =>
  queryOptions({
    queryKey: ['contact', id] as const,
    queryFn: ({ signal }) => api.get<Contact>(apiPath('/api/my/contacts/:contactId', { contactId: id }), signal),
  })

/** The most hits one search asks for; a full page of them is shown with a note that there may be more. */
export const SEARCH_LIMIT = 50

export const searchQuery = (q: string) =>
  queryOptions({
    queryKey: ['contact', 'search', q] as const,
    queryFn: ({ signal }) => api.post<{ items: ContactHit[] }>('/api/my/contacts/search', { q, limit: SEARCH_LIMIT }, signal),
    staleTime: 0,
  })

export type MatchTotals = { matched: number; uncertain: number; unmatched: number }

/**
 * Calls my.matchRun until nothing remains. One call looks up at most 25 company texts and answers how many contacts it did
 * not reach (`remaining`) and the cursor to go on from (`next`); `onLeft` hears the contacts still waiting after each call.
 */
export async function runMatching(importId: string | undefined, onLeft: (left: number) => void): Promise<MatchTotals> {
  const totals: MatchTotals = { matched: 0, uncertain: 0, unmatched: 0 }
  for (let after: string | undefined; ; ) {
    const r = await api.post<MatchTotals & { remaining: number; next: string | null }>('/api/my/matches/run', { ...(importId ? { importId } : {}), ...(after ? { after } : {}) })
    totals.matched += r.matched
    totals.uncertain += r.uncertain
    totals.unmatched += r.unmatched
    if (r.remaining === 0 || r.next === null) return totals
    onLeft(r.remaining)
    after = r.next
  }
}

export interface ListChoice {
  id: string
  name: string
  version: number
  items: number
}

// shortcut: the first 100 private lists, upgrade when someone has more.
export const listChoicesQuery = queryOptions({
  queryKey: ['myList', 'picker'] as const,
  queryFn: ({ signal }) => api.get<Page<ListChoice>>(apiPath('/api/my/lists', {}, { limit: 100 }), signal),
})

// shortcut: the first 200 import batches, upgrade when one user imports more than that many files.
export const batchesQuery = queryOptions({
  queryKey: ['importBatch', 'list'] as const,
  queryFn: ({ signal }) => api.get<Page<ImportBatch>>(apiPath('/api/my/imports', {}, { limit: 200 }), signal),
})

export const matchesQuery = infiniteQueryOptions({
  queryKey: ['match', 'list'] as const,
  initialPageParam: undefined as string | undefined,
  queryFn: ({ pageParam, signal }) => api.get<Page<ContactMatch>>(apiPath('/api/my/matches', {}, { status: 'uncertain', after: pageParam, limit: 50 }), signal),
  getNextPageParam: (last) => last.next ?? undefined,
})

// shortcut: the first 200 lead rows, upgrade when the screen needs more than that for one offer.
export const leadsQuery = (family: string) =>
  queryOptions({
    queryKey: ['contact', 'leads', family] as const,
    queryFn: ({ signal }) => api.post<Page<LeadRow>>('/api/my/leads', { family, limit: 200 }, signal),
  })
